import { getSleeperPlayerMap } from './sleeperKtcLeague.js';
import { pickAmount } from './auctionInflation.js';
import { canonicalTeam, normalizeNameKey, teamRoughMatch } from './playerMatch.js';

const SLEEPER = 'https://api.sleeper.app/v1';

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Sleeper request failed (${response.status}).`);
  }
  return response.json();
};

const formatPlayerName = (p) => {
  if (!p) return '';
  return (
    p.full_name ||
    [p.first_name, p.last_name].filter(Boolean).join(' ') ||
    String(p.player_id || '')
  );
};

const formatPickMetadataName = (meta) => {
  if (!meta) return '';
  return [meta.first_name, meta.last_name].filter(Boolean).join(' ');
};

const positionLabel = (p) =>
  p?.position || p?.fantasy_positions?.[0] || p?.depth_chart_position || '—';

const teamLabel = (p) => p?.team || p?.metadata?.team_abbr || 'FA';

const positionsMatch = (sleeperPos, boardPos) => {
  if (!sleeperPos || !boardPos) return false;
  const s = String(sleeperPos).toUpperCase();
  const b = String(boardPos).toUpperCase();
  if (s === b) return true;
  if ((s === 'PK' || s === 'K') && (b === 'K' || b === 'PK')) return true;
  if ((s === 'DEF' || s === 'DST') && (b === 'DEF' || b === 'DST')) return true;
  return false;
};

const lastNameKey = (name) => normalizeNameKey(name).split(' ').slice(-1)[0] || '';

/**
 * Last resort when full names disagree: the two sources sometimes use
 * different first names for the same player ("Chig" vs "Chigoziem", "Kenny"
 * vs "Kenneth"). Surname plus position plus team is specific enough to be
 * safe, but only when it lands on exactly one board row.
 */
const findBoardRowByLastName = (name, position, team, board) => {
  const wantLast = lastNameKey(name);
  if (!wantLast || wantLast.length < 4) return null;

  const candidates = board.filter((row) => {
    if (row.isPick) return false;
    if (!positionsMatch(position, row.position)) return false;
    if (lastNameKey(row.name) !== wantLast) return false;
    return canonicalTeam(team) && canonicalTeam(team) === canonicalTeam(row.team);
  });

  return candidates.length === 1 ? candidates[0] : null;
};

const findBoardRowForSleeperPlayer = (sp, board) => {
  const fullName = formatPlayerName(sp);
  const wantName = normalizeNameKey(fullName);
  if (!wantName) return null;

  const wantPos = positionLabel(sp);
  const wantTeam = teamLabel(sp);

  const candidates = board.filter((row) => {
    if (row.isPick) return false;
    if (!positionsMatch(wantPos, row.position)) return false;
    if (normalizeNameKey(row.name) !== wantName) return false;
    if (!teamRoughMatch(wantTeam, row.team)) return false;
    return true;
  });

  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    const withTeam = candidates.filter((c) => teamRoughMatch(wantTeam, c.team));
    if (withTeam.length === 1) return withTeam[0];
  }

  return findBoardRowByLastName(fullName, wantPos, wantTeam, board);
};

const findBoardRowFromPickMetadata = (pick, board) => {
  const meta = pick?.metadata;
  if (!meta) return null;

  const wantName = normalizeNameKey(formatPickMetadataName(meta));
  const wantPos = meta.position;
  const wantTeam = meta.team;

  if (!wantName || !wantPos) return null;

  const candidates = board.filter((row) => {
    if (row.isPick) return false;
    if (!positionsMatch(wantPos, row.position)) return false;
    if (normalizeNameKey(row.name) !== wantName) return false;
    if (!teamRoughMatch(wantTeam, row.team)) return false;
    return true;
  });

  if (candidates.length === 1) return candidates[0];
  if (candidates.length) return candidates[0];

  return findBoardRowByLastName(formatPickMetadataName(meta), wantPos, wantTeam, board);
};

export const fetchLeagueDrafts = async (leagueId) => {
  const lid = leagueId?.trim();
  if (!lid) throw new Error('Enter your Sleeper league ID.');

  const drafts = await fetchJson(`${SLEEPER}/league/${lid}/drafts`);
  if (!Array.isArray(drafts)) return [];
  return drafts;
};

export const fetchDraft = async (draftId) => {
  if (!draftId) throw new Error('Select a draft.');
  return fetchJson(`${SLEEPER}/draft/${draftId}`);
};

export const fetchDraftPicks = async (draftId) => {
  if (!draftId) throw new Error('Select a draft.');
  const picks = await fetchJson(`${SLEEPER}/draft/${draftId}/picks`);
  return Array.isArray(picks) ? picks : [];
};

export const formatDraftLabel = (draft) => {
  const name = draft?.metadata?.name || 'Sleeper draft';
  const season = draft?.season || '';
  const status = draft?.status || 'unknown';
  const type = draft?.type || '';
  const parts = [name];
  if (season) parts.push(String(season));
  if (type) parts.push(type);
  parts.push(status.replace(/_/g, ' '));
  return parts.join(' · ');
};

/**
 * Prefer an in-progress draft, then pre-draft, then the most recent entry.
 */
export const pickPreferredDraft = (drafts) => {
  if (!drafts?.length) return null;

  const byStatus = (status) => drafts.find((d) => d.status === status);
  const drafting = byStatus('drafting');
  if (drafting) return drafting;

  const preDraft = byStatus('pre_draft');
  if (preDraft) return preDraft;

  return drafts[0];
};

export const pollIntervalForStatus = (status) => {
  if (status === 'drafting') return 4000;
  if (status === 'pre_draft') return 12000;
  if (status === 'complete') return 30000;
  return 8000;
};

const buildTeamNameMap = (teams) =>
  new Map((teams || []).map((t) => [String(t.rosterId), t.teamName]));

/**
 * Map Sleeper draft picks onto KTC board rows for live sync.
 */
export const mapPicksToBoardState = async (picks, board, teamNameByRoster = new Map()) => {
  const sleeperPlayers = await getSleeperPlayerMap();
  const draftedIds = new Set();
  const draftLog = [];
  const unmatched = [];

  const sorted = [...(picks || [])].sort((a, b) => Number(a.pick_no) - Number(b.pick_no));

  for (const pick of sorted) {
    if (!pick?.player_id) continue;

    const sp = sleeperPlayers[pick.player_id];
    const row =
      findBoardRowForSleeperPlayer(sp, board) || findBoardRowFromPickMetadata(pick, board);

    const sleeperName =
      formatPlayerName(sp) || formatPickMetadataName(pick.metadata) || `Player ${pick.player_id}`;
    const sleeperPos = positionLabel(sp) || pick.metadata?.position || '—';
    const teamName = teamNameByRoster.get(String(pick.roster_id)) || `Roster ${pick.roster_id}`;

    if (!row) {
      unmatched.push({
        pickNo: pick.pick_no,
        name: sleeperName,
        position: sleeperPos,
        rosterId: pick.roster_id,
        teamName,
      });
      continue;
    }

    draftedIds.add(row.id);
    draftLog.push({
      id: row.id,
      name: row.name,
      position: row.position,
      // Carried so the log's avatar has a team logo to fall back on.
      team: row.team,
      value: row.value,
      at: Number(pick.pick_no) * 1000,
      pickNo: pick.pick_no,
      round: pick.round,
      rosterId: pick.roster_id,
      teamName,
      // Only auction picks carry one. It is the single most useful number the
      // picks feed has: what the room was actually willing to pay, against
      // what this board said he was worth.
      amount: pickAmount(pick),
      source: 'sleeper',
    });
  }

  draftLog.sort((a, b) => Number(b.pickNo) - Number(a.pickNo));

  return {
    draftedIds,
    draftLog,
    unmatched,
    pickCount: sorted.filter((p) => p?.player_id).length,
  };
};

/**
 * One sync tick: fetch draft meta + picks and map to board state.
 */
export const syncDraftFromSleeper = async ({ draftId, board, teams = [] }) => {
  const [draft, picks] = await Promise.all([fetchDraft(draftId), fetchDraftPicks(draftId)]);
  const teamNameByRoster = buildTeamNameMap(teams);
  const mapped = await mapPicksToBoardState(picks, board, teamNameByRoster);

  const totalPicks =
    Number(draft?.settings?.teams || 0) * Number(draft?.settings?.rounds || 0) || null;

  return {
    draft,
    ...mapped,
    totalPicks,
    status: draft?.status || 'unknown',
    draftName: draft?.metadata?.name || 'Sleeper draft',
    pollMs: pollIntervalForStatus(draft?.status),
  };
};

export { findBoardRowForSleeperPlayer };
